import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, PageHeader, Stat, Pill } from "../components/UI";
import { Footprints, MapPin, TrendingUp, Users } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const fmt = (n) => { if (n >= 1e6) return (n / 1e6).toFixed(1) + "M"; if (n >= 1e3) return (n / 1e3).toFixed(1) + "K"; return String(n); };

export default function Footfall() {
  const [data, setData] = useState(null);
  useEffect(() => { (async () => { const { data } = await api.get("/reports/overview"); setData(data); })(); }, []);
  const visits = data?.kpis?.visits || 0;
  const cpv = data?.kpis?.cpv || 0;

  // Simulated visit cohorts
  const cohorts = [
    { label: "Exposed cohort visits", value: visits, color: "text-cyan-300" },
    { label: "Control cohort visits", value: Math.round(visits * 0.6), color: "text-slate-300" },
    { label: "Incremental visits", value: Math.round(visits * 0.4), color: "text-emerald-300" },
    { label: "Visit lift", value: "+38%", color: "brand-gradient" },
  ];

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="Foot-Traffic Attribution"
        title="Footfall Attribution"
        subtitle="Compare exposed vs. control cohorts to measure real-world store visits and visit lift."
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cohorts.map((c, i) => (
          <Card key={i}>
            <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">{c.label}</div>
            <div className={`mt-2 font-display text-3xl font-bold ${c.color}`}>{typeof c.value === "number" ? fmt(c.value) : c.value}</div>
          </Card>
        ))}
      </div>

      <Card>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">Visits over time</div>
            <h3 className="font-display text-xl font-bold mt-1">Weekly attributed visits</h3>
          </div>
          <Pill status="Active">Cost per visit: ${cpv}</Pill>
        </div>
        {data && (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.trend}>
              <defs>
                <linearGradient id="vg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="week" stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} tickFormatter={fmt} />
              <Tooltip contentStyle={{ background: "#0a0e1a", border: "1px solid rgba(34,211,238,0.3)", borderRadius: 8 }} />
              <Area type="monotone" dataKey="visits" stroke="#10b981" fill="url(#vg)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card>
        <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-3">Privacy & methodology</div>
        <ul className="space-y-2 text-sm text-slate-300">
          <li className="flex gap-2"><MapPin className="w-4 h-4 text-cyan-300 shrink-0 mt-0.5" /> Geo-conversion zones are drawn at advertiser locations, excluding roads, sidewalks, and adjacent properties.</li>
          <li className="flex gap-2"><Users className="w-4 h-4 text-cyan-300 shrink-0 mt-0.5" /> Exposed cohort is matched 1:1 against a behavior-similar control cohort.</li>
          <li className="flex gap-2"><TrendingUp className="w-4 h-4 text-cyan-300 shrink-0 mt-0.5" /> Lat/long precision validated (≥4 decimals); impossible-travel events filtered.</li>
          <li className="flex gap-2"><Footprints className="w-4 h-4 text-cyan-300 shrink-0 mt-0.5" /> Minimum dwell time and frequency thresholds applied before counting a visit.</li>
        </ul>
      </Card>
    </div>
  );
}
